import { useReadContract, useWriteContract, useReadContracts, useAccount, useWaitForTransactionReceipt, useChainId } from 'wagmi';
import { Abi } from 'viem';
import { getContractsByChainId } from '../../../core/contracts/config';
import { useMemo } from 'react';

export interface Video {
  videoUrl: string;
  postUrl: string;
  thresholds: {
    threshold1: bigint;
    threshold2: bigint;
    threshold3: bigint;
  };
  platform: string;
  viewsDeadline: bigint;
  bettingDeadline: bigint;
  views: bigint;
  isViewsSet: boolean;
  creator: string;
  feePercentage: bigint;
  minBetAmount: bigint;
  maxBetAmount: bigint;
}

export interface Bet {
  bettor: string;
  videoId: bigint;
  amount: bigint;
  thresholdIndex: bigint;
  timestamp: bigint;
  claimed: boolean;
}

/**
 * Hook to get the current contract configuration based on the connected network
 */
export function useContractConfig() {
  const chainId = useChainId();
  return useMemo(() => getContractsByChainId(chainId), [chainId]);
}

export function useActiveVideos() {
  const CONTRACTS = useContractConfig();
  // Get active video IDs
  const { data: activeVideoIds, isLoading: isLoadingIds, error: errorIds } = useReadContract({
    ...CONTRACTS.VideoRegistry,
    functionName: 'getActiveVideos',
  });

  return {
    videoIds: activeVideoIds as bigint[] | undefined,
    isLoading: isLoadingIds,
    error: errorIds,
  };
}

export function useVideo(videoId: bigint | undefined) {
  const CONTRACTS = useContractConfig();
  const { data: video, isLoading, error } = useReadContract({
    ...CONTRACTS.VideoRegistry,
    functionName: 'getVideo',
    args: videoId !== undefined ? [videoId] : undefined,
  });

  return {
    video: video as Video | undefined,
    isLoading,
    error,
  };
}

export function useVideoBets(videoId: bigint | undefined) {
  const CONTRACTS = useContractConfig();
  const { data: betIds, isLoading, error } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'getVideoBets',
    args: videoId !== undefined ? [videoId] : undefined,
  });

  return {
    betIds: betIds as bigint[] | undefined,
    isLoading,
    error,
  };
}

export function usePoolInfo(videoId: bigint | undefined) {
  const CONTRACTS = useContractConfig();
  const { data: poolInfo, isLoading, error } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'getPoolInfo',
    args: videoId !== undefined ? [videoId] : undefined,
  });

  const result = poolInfo as [bigint, bigint] | undefined;

  return {
    totalPool: result?.[0],
    winnerPool: result?.[1],
    isLoading,
    error,
  };
}

// Betting hooks
export function usePlaceBet() {
  const CONTRACTS = useContractConfig();
  const { writeContract, isPending, isSuccess, error, data: hash } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });


  const placeBet = (videoId: bigint, thresholdIndex: bigint, value: bigint) => {
    writeContract({
      ...CONTRACTS.BettingManager,
      functionName: 'placeBet',
      args: [videoId, thresholdIndex],
      value,
    });
  };

  return {
    placeBet,
    isPending,
    isSuccess,
    isConfirming,
    isConfirmed,
    error,
    hash,
  };
}

export function useUserBets() {
  const CONTRACTS = useContractConfig();
  const { address } = useAccount();

  const { data: betIds, isLoading, error, refetch } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'getUserBets',
    args: address ? [address] : undefined,
  });

  return {
    betIds: betIds as bigint[] | undefined,
    isLoading,
    error,
    refetch,
  };
}

export function useBet(betId: bigint | undefined) {
  const CONTRACTS = useContractConfig();
  const { data: bet, isLoading, error, refetch } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'getBet',
    args: betId !== undefined ? [betId] : undefined,
  });

  return {
    bet: bet as Bet | undefined,
    isLoading,
    error,
    refetch,
  };
}

export function useIsWinningBet(betId: bigint | undefined) {
  const CONTRACTS = useContractConfig();
  const { data: isWinning, isLoading, error } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'isWinningBet',
    args: betId !== undefined ? [betId] : undefined,
  });

  return {
    isWinning: isWinning as boolean | undefined,
    isLoading,
    error,
  };
}

export function useCalculatePayout(betId: bigint | undefined) {
  const CONTRACTS = useContractConfig();
  const { data: payout, isLoading, error } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'calculatePayout',
    args: betId !== undefined ? [betId] : undefined,
  });

  return {
    payout: payout as bigint | undefined,
    isLoading,
    error,
  };
}

export function useClaimReward() {
  const CONTRACTS = useContractConfig();
  const { writeContract, isPending, isSuccess, error, data: hash } = useWriteContract();

  const { isLoading: isConfirming, isSuccess: isConfirmed } = useWaitForTransactionReceipt({
    hash,
  });

  const claimReward = (betId: bigint) => {
    writeContract({
      ...CONTRACTS.BettingManager,
      functionName: 'claimReward',
      args: [betId],
    });
  };

  return {
    claimReward,
    isPending,
    isSuccess,
    isConfirming,
    isConfirmed,
    error,
    hash,
  };
}

// Hook to calculate total pot for a video by summing all bet amounts
export function useVideoTotalPot(betIds: bigint[] | undefined) {
  const CONTRACTS = useContractConfig();
  const contracts = betIds?.map(betId => ({
    address: CONTRACTS.BettingManager.address,
    abi: CONTRACTS.BettingManager.abi as Abi,
    functionName: 'getBet',
    args: [betId],
  })) || [];

  const { data: bets, isLoading } = useReadContracts({
    contracts,
  });

  // Calculate total pot
  const totalPot = bets?.reduce((sum: bigint, result: any) => {
    const bet = result.result as Bet | undefined;
    if (bet?.amount) {
      return sum + bet.amount;
    }
    return sum;
  }, 0n) || 0n;

  return {
    totalPot,
    isLoading,
  };
}
// Hook to fetch all videos and filter by status
export function useAllVideos() {
  const CONTRACTS = useContractConfig();
  // 1. Get total video count
  const { data: totalVideos, isLoading: isLoadingCount } = useReadContract({
    ...CONTRACTS.VideoRegistry,
    functionName: 'getTotalVideos',
  });

  // 2. Prepare contract calls for all videos
  const videoCount = totalVideos ? Number(totalVideos) : 0;
  const videoContracts = Array.from({ length: videoCount }, (_, i) => ({
    ...CONTRACTS.VideoRegistry,
    abi: CONTRACTS.VideoRegistry.abi as Abi,
    functionName: 'getVideo',
    args: [BigInt(i)],
  }));

  // 3. Fetch all videos
  const { data: videosData, isLoading: isLoadingVideos, refetch } = useReadContracts({
    contracts: videoContracts,
    query: {
      refetchInterval: 5000, // Poll every 5 seconds
    }
  });

  // 4. Process and filter videos
  const now = Math.floor(Date.now() / 1000);

  const allVideos = videosData?.map((result, index) => {
    if (result.status === 'success') {
      return { ...(result.result as Video), id: BigInt(index) };
    }
    return null;
  }).filter((v): v is Video & { id: bigint } => v !== null) || [];

  const activeVideos = allVideos.filter(v => now < Number(v.bettingDeadline));

  const pendingVideos = allVideos.filter(v =>
    now >= Number(v.bettingDeadline) && !v.isViewsSet
  );

  const resolvedVideos = allVideos.filter(v => v.isViewsSet);

  return {
    allVideos,
    activeVideos,
    pendingVideos,
    resolvedVideos,
    isLoading: isLoadingCount || isLoadingVideos,
    error: null as Error | null,
    refetch,
  };
}

// Hook to fetch user bets grouped by video
export function useUserBetsGrouped() {
  const CONTRACTS = useContractConfig();
  const { address } = useAccount();

  // 1. Get user bet IDs
  const { data: betIds, isLoading: isLoadingIds, refetch: refetchIds } = useReadContract({
    ...CONTRACTS.BettingManager,
    functionName: 'getUserBets',
    args: address ? [address] : undefined,
    query: {
      refetchInterval: 5000,
    }
  });

  // 2. Fetch bet details
  const betContracts = (betIds as bigint[])?.map(id => ({
    ...CONTRACTS.BettingManager,
    abi: CONTRACTS.BettingManager.abi as Abi,
    functionName: 'getBet',
    args: [id],
  })) || [];

  const { data: betsData, isLoading: isLoadingBets, refetch: refetchBets } = useReadContracts({
    contracts: betContracts,
    query: {
      refetchInterval: 5000,
    }
  });

  // 3. Group bets by videoId and fetch video details
  const bets = betsData?.map((result, index) => {
    if (result.status === 'success') {
      return { ...(result.result as Bet), id: (betIds as bigint[])[index] };
    }
    return null;
  }).filter((b): b is Bet & { id: bigint } => b !== null) || [];

  // Get unique video IDs from bets
  const uniqueVideoIds = Array.from(new Set(bets.map(b => b.videoId)));

  // Fetch video details for these videos
  const videoContracts = uniqueVideoIds.map(id => ({
    ...CONTRACTS.VideoRegistry,
    abi: CONTRACTS.VideoRegistry.abi as Abi,
    functionName: 'getVideo',
    args: [id],
  }));

  const { data: videosData, isLoading: isLoadingVideos, refetch: refetchVideos } = useReadContracts({
    contracts: videoContracts,
    query: {
      refetchInterval: 5000,
    }
  });

  // 4. Combine data
  const groupedBets = uniqueVideoIds.map((videoId, index) => {
    const videoResult = videosData?.[index];
    const video = videoResult?.status === 'success' ? (videoResult.result as Video) : null;

    if (!video) return null;

    const videoBets = bets.filter(b => b.videoId === videoId);
    const totalWagered = videoBets.reduce((sum, b) => sum + b.amount, 0n);

    return {
      video: { ...video, id: videoId },
      bets: videoBets,
      totalWagered,
    };
  }).filter(g => g !== null);

  const refetch = () => {
    refetchIds();
    refetchBets();
    refetchVideos();
  };

  return {
    groupedBets,
    isLoading: isLoadingIds || isLoadingBets || isLoadingVideos,
    refetch,
  };
}
